/**
 * Date picker with ISO week number column.
 *
 * The native <input type="date"> pop-up cannot be customised to include a
 * week-number column — that's a browser-level limitation. This component
 * replaces the native picker with a react-day-picker calendar in a floating
 * panel, and passes `showWeekNumber` so the ISO week appears as the first
 * column of the grid.
 *
 * Value is a `YYYY-MM-DD` string (same contract as `<input type="date">`).
 */
import React, { useEffect, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Calendar as CalendarIcon } from "lucide-react";

function parseISODate(s) {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return undefined;
  const dt = new Date(y, m - 1, d);
  return isNaN(dt) ? undefined : dt;
}

function toISODate(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/** Compact DD/MM (no year) — button label uses this to save horizontal space. */
function toShortDate(d) {
  if (!d) return "";
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${dd}/${m}`;
}

function isoWeekOf(d) {
  if (!d) return null;
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
  return { year: t.getUTCFullYear(), week: weekNo };
}

export default function WeekDatePicker({
  value,
  onChange,
  placeholder = "dd/mm",
  className = "",
  testId = "",
  inputClassName = "",
  minDate = null,          // ISO YYYY-MM-DD — anything before is disabled
  maxDate = null,          // ISO YYYY-MM-DD — anything after is disabled
  openOnMount = false,     // for click-to-edit inline pickers
}) {
  const [open, setOpen] = useState(!!openOnMount);
  const [month, setMonth] = useState(parseISODate(value) || new Date());
  const wrapRef = useRef(null);

  useEffect(() => {
    if (value) setMonth(parseISODate(value) || new Date());
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selectedDate = parseISODate(value);
  const iw = isoWeekOf(selectedDate);

  return (
    <div ref={wrapRef} className={`relative inline-block ${className}`}>
      <div className="flex items-center gap-1.5">
        <button type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid={testId ? `${testId}-btn` : undefined}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded bg-slate-900/60 border border-white/10 hover:border-cyan-400/60 text-xs text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400 ${inputClassName}`}>
          <CalendarIcon className="w-3 h-3 text-slate-400"/>
          <span className={selectedDate ? "font-mono" : "text-slate-500"}
                title={selectedDate ? toISODate(selectedDate) : ""}>
            {selectedDate ? toShortDate(selectedDate) : placeholder}
          </span>
        </button>
        {iw && (
          <span className="text-[9px] uppercase tracking-widest font-black text-cyan-300 bg-cyan-500/10 px-1.5 py-0.5 rounded"
                data-testid={testId ? `${testId}-week` : undefined}
                title={`ISO week ${iw.year}-W${String(iw.week).padStart(2, "0")}`}>
            W{String(iw.week).padStart(2, "0")}
          </span>
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 rounded-xl border border-white/10 bg-[#0f1a2e] shadow-2xl p-2"
             data-testid={testId ? `${testId}-panel` : undefined}
             style={{ colorScheme: "dark" }}>
          <DayPicker mode="single"
                     selected={selectedDate}
                     month={month}
                     onMonthChange={setMonth}
                     disabled={[
                       ...(minDate ? [{ before: parseISODate(minDate) }] : []),
                       ...(maxDate ? [{ after: parseISODate(maxDate) }] : []),
                     ]}
                     showWeekNumber
                     weekStartsOn={1}
                     ISOWeek
                     onSelect={(d) => {
                       onChange(toISODate(d));
                       setOpen(false);
                     }}
                     className="rdp-nordic"
                     styles={{
                       caption: { color: "#e2e8f0", fontWeight: 700 },
                       nav_button: { color: "#67e8f9" },
                       head_cell: { color: "#94a3b8", fontSize: 10, textTransform: "uppercase" },
                       cell: { color: "#e2e8f0", width: 34, height: 34 },
                       day: { color: "#e2e8f0", width: 30, height: 30, borderRadius: 6 },
                       day_selected: { background: "#06b6d4", color: "#0f1a2e", fontWeight: 800 },
                       day_today: { border: "1px solid #22d3ee" },
                       weeknumber: {
                         color: "#67e8f9",
                         fontSize: 10,
                         fontWeight: 900,
                         letterSpacing: "0.05em",
                         width: 30,
                       },
                     }}/>
          <div className="flex items-center justify-between px-2 pt-2 pb-1 border-t border-white/5 mt-1">
            <button type="button"
                    onClick={() => { onChange(""); setOpen(false); }}
                    className="text-[10px] uppercase tracking-widest font-black text-slate-400 hover:text-red-300">
              Clear
            </button>
            <button type="button"
                    onClick={() => { onChange(toISODate(new Date())); setOpen(false); }}
                    className="text-[10px] uppercase tracking-widest font-black text-cyan-300 hover:text-cyan-200">
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
